import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Card,
  Space,
  Statistic,
  Row,
  Col,
  Progress,
  Button,
  Divider,
  Upload,
  Modal,
  message,
  Form,
  Input,
  Select,
  Table,
  Tabs,
  Alert,
  Badge,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  DeleteOutlined,
  SafetyOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useReconciliation } from "../../../hooks/finance/useReconciliation";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Text } = Typography;

const PURPOSE_OPTIONS = [
  { label: "Tuition Fees", value: "Tuition Fees" },
  { label: "Custom Fee", value: "Custom Fee" },
  { label: "Transport Fees", value: "Transport Fees" },
  { label: "BOT Clearance", value: "BOT Clearance" },
  { label: "Admission Fees", value: "Admission Fees" },
];

const ReconciliationDetailDrawer = ({
  visible,
  onClose,
  reconciliationId,
  onComplete,
  onMarkForReview,
}) => {
  const navigate = useNavigate();
  const {
    reconciliationDetails,
    loading,
    fetchReconciliationDetails,
    updateReconciliation,
  } = useReconciliation();

  const [uploading, setUploading] = useState(false);

  // Payment breakdown state
  const [pendingPayments, setPendingPayments] = useState([]);
  const [suspensePayments, setSuspensePayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Validation modal state
  const [validateModalVisible, setValidateModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validateForm] = Form.useForm();

  useEffect(() => {
    if (visible && reconciliationId) {
      fetchReconciliationDetails(reconciliationId);
    }
    if (!visible) {
      setPendingPayments([]);
      setSuspensePayments([]);
      setSelectedPayment(null);
    }
  }, [visible, reconciliationId]);

  // Fetch payment breakdown once we have the reconciliation date
  useEffect(() => {
    if (reconciliationDetails?.reconciliationDate) {
      fetchPaymentsForDate(reconciliationDetails.reconciliationDate);
    }
  }, [reconciliationDetails?.reconciliationDate]);

  const fetchPaymentsForDate = async (date) => {
    setPaymentsLoading(true);
    try {
      const baseParams = new URLSearchParams({
        date_from: date,
        date_to: date,
      });

      const pendingParams = new URLSearchParams({
        ...Object.fromEntries(baseParams),
        status: "Pending Validation",
      });
      const suspenseParams = new URLSearchParams({
        ...Object.fromEntries(baseParams),
        status: "Suspense",
      });

      const [pendingRes, suspenseRes] = await Promise.all([
        Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.finance.payments}?${pendingParams}`,
          null,
          navigate,
        ),
        Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.finance.payments}?${suspenseParams}`,
          null,
          navigate,
        ),
      ]);

      if (pendingRes?.ok) {
        const d = pendingRes.data;
        setPendingPayments(Array.isArray(d) ? d : d?.results || []);
      }
      if (suspenseRes?.ok) {
        const d = suspenseRes.data;
        setSuspensePayments(Array.isArray(d) ? d : d?.results || []);
      }
    } catch (err) {
      message.error("Failed to load payment breakdown");
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleOpenValidateModal = (payment) => {
    setSelectedPayment(payment);
    validateForm.setFieldsValue({
      purpose: payment.purpose || undefined,
      correctCode: payment.paymentCode || undefined,
      notes: undefined,
    });
    setValidateModalVisible(true);
  };

  const handleValidate = async () => {
    try {
      const values = await validateForm.validateFields();
      setValidating(true);

      const payload = {};
      if (values.purpose) payload.purpose = values.purpose;
      if (values.correctCode) payload.correctCode = values.correctCode;
      if (values.notes) payload.notes = values.notes;

      const url = domainSettings.urls.finance.paymentValidate.replace(
        ":id",
        selectedPayment.paymentId,
      );
      const res = await Utilities.fetchWithAuth("POST", url, payload, navigate);

      if (res?.ok) {
        message.success("Payment validated successfully");
        setValidateModalVisible(false);
        validateForm.resetFields();
        setSelectedPayment(null);

        // Remove from the appropriate list
        if (selectedPayment.status === "Pending Validation") {
          setPendingPayments((prev) =>
            prev.filter((p) => p.paymentId !== selectedPayment.paymentId),
          );
        } else {
          setSuspensePayments((prev) =>
            prev.filter((p) => p.paymentId !== selectedPayment.paymentId),
          );
        }
        // Refresh summary stats
        fetchReconciliationDetails(reconciliationId);
      } else {
        Utilities.showError?.(res, "Validation failed") ||
          message.error("Validation failed");
      }
    } catch (err) {
      // form validation error — do nothing, antd shows the messages
    } finally {
      setValidating(false);
    }
  };

  // --- Bank statement handlers ---
  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const result = await updateReconciliation(reconciliationId, {
        bankStatementFile: file,
      });
      if (result) message.success("Bank statement uploaded successfully");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleFileRemove = () => {
    Modal.confirm({
      title: "Remove Bank Statement",
      content: "Are you sure you want to remove the bank statement file?",
      okText: "Yes, Remove",
      okType: "danger",
      onOk: async () => {
        const result = await updateReconciliation(reconciliationId, {
          removeBankStatement: true,
        });
        if (result) message.success("Bank statement removed");
      },
    });
  };

  // --- Helpers ---
  const getStatusColor = (status) =>
    ({
      Completed: "green",
      "In Progress": "blue",
      "Requires Review": "orange",
    })[status] || "default";

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getReconciliationRate = () => {
    if (!reconciliationDetails) return 0;
    const total = parseFloat(reconciliationDetails.totalPayments) || 0;
    const valid = parseFloat(reconciliationDetails.totalValidPayments) || 0;
    return total > 0 ? Math.floor((valid / total) * 100) : 0;
  };

  const getPendingAmount = () => {
    if (!reconciliationDetails) return 0;
    const total = parseFloat(reconciliationDetails.totalPayments) || 0;
    const valid = parseFloat(reconciliationDetails.totalValidPayments) || 0;
    const suspense =
      parseFloat(reconciliationDetails.totalSuspensePayments) || 0;
    return Math.max(0, total - valid - suspense);
  };

  const isSuspense = selectedPayment?.status === "Suspense";

  // --- Payment table columns ---
  const paymentColumns = [
    {
      title: "Reference",
      dataIndex: "paymentReference",
      width: 130,
      render: (v) => (
        <Tooltip title={v}>
          <Text style={{ fontSize: 12 }} copyable={{ text: v }}>
            {v?.length > 14 ? `${v.slice(0, 14)}…` : v}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Student",
      dataIndex: "studentName",
      width: 130,
      render: (v) =>
        v ? (
          <Text style={{ fontSize: 12 }}>{v}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      width: 110,
      render: (v) =>
        v ? (
          <Tag style={{ fontSize: 11 }}>{v}</Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 100,
      align: "right",
      render: (v) => (
        <Text strong style={{ fontSize: 12 }}>
          UGX {formatCurrency(v)}
        </Text>
      ),
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      width: 105,
      render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<SafetyOutlined />}
          onClick={() => handleOpenValidateModal(record)}
        >
          Validate
        </Button>
      ),
    },
  ];

  if (!reconciliationDetails && !loading) return null;

  const rate = getReconciliationRate();
  const pendingAmount = getPendingAmount();

  const tabItems = [
    {
      key: "pending",
      label: (
        <Space size={6}>
          <ClockCircleOutlined style={{ color: "#faad14" }} />
          <span>Pending Validation</span>
          {pendingPayments.length > 0 && (
            <Badge count={pendingPayments.length} color="#faad14" />
          )}
        </Space>
      ),
      children: (
        <Table
          dataSource={pendingPayments}
          columns={paymentColumns}
          rowKey="paymentId"
          loading={paymentsLoading}
          size="small"
          pagination={
            pendingPayments.length > 8
              ? { pageSize: 8, size: "small", showSizeChanger: false }
              : false
          }
          scroll={{ x: 660 }}
          locale={{
            emptyText: (
              <Text type="secondary">No payments pending validation</Text>
            ),
          }}
        />
      ),
    },
    {
      key: "suspense",
      label: (
        <Space size={6}>
          <WarningOutlined style={{ color: "#ff4d4f" }} />
          <span>In Suspense</span>
          {suspensePayments.length > 0 && (
            <Badge count={suspensePayments.length} color="#ff4d4f" />
          )}
        </Space>
      ),
      children: (
        <Table
          dataSource={suspensePayments}
          columns={paymentColumns}
          rowKey="paymentId"
          loading={paymentsLoading}
          size="small"
          pagination={
            suspensePayments.length > 8
              ? { pageSize: 8, size: "small", showSizeChanger: false }
              : false
          }
          scroll={{ x: 660 }}
          locale={{
            emptyText: <Text type="secondary">No payments in suspense</Text>,
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Drawer
        title={
          <Space>
            <UnorderedListOutlined />
            <span>Reconciliation Details</span>
            {reconciliationDetails && (
              <Tag color={getStatusColor(reconciliationDetails.status)}>
                {reconciliationDetails.status}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={visible}
        width={720}
        extra={
          reconciliationDetails && (
            <Space>
              {reconciliationDetails.status !== "Completed" && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    onComplete(reconciliationDetails);
                    onClose();
                  }}
                >
                  Complete
                </Button>
              )}
              {(reconciliationDetails.status === "In Progress" ||
                reconciliationDetails.status === "Completed") && (
                <Button
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => {
                    onMarkForReview(reconciliationDetails);
                    onClose();
                  }}
                >
                  Request Review
                </Button>
              )}
            </Space>
          )
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : (
          reconciliationDetails && (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {/* Summary Statistics */}
              <Card title="Financial Summary" size="small">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Total Payments"
                      value={formatCurrency(
                        reconciliationDetails.totalPayments,
                      )}
                      prefix="UGX"
                      valueStyle={{ color: "#1890ff", fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Valid Payments"
                      value={formatCurrency(
                        reconciliationDetails.totalValidPayments,
                      )}
                      prefix="UGX"
                      valueStyle={{ color: "#52c41a", fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="In Suspense"
                      value={formatCurrency(
                        reconciliationDetails.totalSuspensePayments,
                      )}
                      prefix="UGX"
                      valueStyle={{ color: "#ff4d4f", fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Pending Validation"
                      value={formatCurrency(pendingAmount)}
                      prefix="UGX"
                      valueStyle={{ color: "#faad14", fontSize: 16 }}
                    />
                  </Col>
                </Row>
              </Card>

              {/* Progress Bar */}
              <Card size="small">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 8,
                  }}
                >
                  <strong>Reconciliation Progress</strong>
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      lineHeight: 1,
                      color:
                        rate === 100
                          ? "#52c41a"
                          : rate >= 80
                            ? "#1890ff"
                            : "#ff4d4f",
                    }}
                  >
                    {rate}%
                  </span>
                </div>
                <Progress
                  percent={rate}
                  showInfo={false}
                  status={
                    rate === 100
                      ? "success"
                      : rate >= 80
                        ? "active"
                        : "exception"
                  }
                  strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
                />
              </Card>

              {/* Reconciliation Info */}
              <Card title="Reconciliation Information" size="small">
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Reconciliation Date">
                    <strong>
                      {reconciliationDetails.reconciliationDate
                        ? dayjs(
                            reconciliationDetails.reconciliationDate,
                          ).format("dddd, DD MMMM YYYY")
                        : "-"}
                    </strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Count">
                    <Tag color="blue">
                      {reconciliationDetails.paymentCount || 0}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Suspense Count">
                    <Tag
                      color={
                        reconciliationDetails.suspenseCount > 0
                          ? "red"
                          : "default"
                      }
                    >
                      {reconciliationDetails.suspenseCount || 0}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(reconciliationDetails.status)}>
                      {reconciliationDetails.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Reconciled By">
                    {reconciliationDetails.reconciledByName || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {reconciliationDetails.createdAt
                      ? dayjs(reconciliationDetails.createdAt).format(
                          "DD MMM YYYY HH:mm",
                        )
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Updated">
                    {reconciliationDetails.updatedAt
                      ? dayjs(reconciliationDetails.updatedAt).format(
                          "DD MMM YYYY HH:mm",
                        )
                      : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Payment Breakdown — Pending & Suspense tabs */}
              <Card
                title={
                  <Space>
                    <UnorderedListOutlined />
                    <span>Payments Breakdown</span>
                  </Space>
                }
                size="small"
                bodyStyle={{ padding: "0 0 8px 0" }}
              >
                <Tabs
                  defaultActiveKey="pending"
                  size="small"
                  tabBarStyle={{
                    marginBottom: 0,
                    paddingLeft: 12,
                    paddingRight: 12,
                  }}
                  items={tabItems}
                />
              </Card>

              {/* Notes */}
              {reconciliationDetails.notes && (
                <Card title="Notes" size="small">
                  <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                    {reconciliationDetails.notes}
                  </p>
                </Card>
              )}

              {/* Bank Statement */}
              <Card title="Bank Statement" size="small">
                {reconciliationDetails.bankStatementFile ? (
                  <Space>
                    <Button
                      type="primary"
                      icon={<FileTextOutlined />}
                      href={reconciliationDetails.bankStatementFile}
                      target="_blank"
                    >
                      View Statement
                    </Button>
                    <Upload
                      beforeUpload={handleFileUpload}
                      showUploadList={false}
                      accept=".pdf,.xlsx,.xls,.csv"
                    >
                      <Button icon={<UploadOutlined />} loading={uploading}>
                        Replace
                      </Button>
                    </Upload>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleFileRemove}
                    >
                      Remove
                    </Button>
                  </Space>
                ) : (
                  <Upload
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                    accept=".pdf,.xlsx,.xls,.csv"
                  >
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      Upload Bank Statement
                    </Button>
                  </Upload>
                )}
              </Card>

              <Divider />

              {/* Action Hints */}
              {reconciliationDetails.status === "In Progress" && (
                <Card
                  size="small"
                  style={{ background: "#fffbe6", borderColor: "#ffe58f" }}
                >
                  <Space direction="vertical">
                    <strong>Next Steps:</strong>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        Validate pending payments using the breakdown above
                      </li>
                      <li>
                        Resolve suspense entries with correct payment codes
                      </li>
                      <li>Compare with bank statement if uploaded</li>
                      <li>Mark as "Complete" when all items are reconciled</li>
                    </ul>
                  </Space>
                </Card>
              )}

              {reconciliationDetails.status === "Requires Review" && (
                <Card
                  size="small"
                  style={{ background: "#fff7e6", borderColor: "#ffd591" }}
                >
                  <Space direction="vertical">
                    <strong>Review Required:</strong>
                    <p style={{ margin: 0 }}>
                      This reconciliation has been flagged for review. Please
                      check:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Unresolved suspense entries</li>
                      <li>Discrepancies with bank statement</li>
                      <li>Any notes or comments</li>
                    </ul>
                  </Space>
                </Card>
              )}
            </Space>
          )
        )}
      </Drawer>

      {/* Validate Payment Modal */}
      <Modal
        title={
          <Space>
            <SafetyOutlined
              style={{ color: isSuspense ? "#ff4d4f" : "#1890ff" }}
            />
            <span>Validate Payment</span>
          </Space>
        }
        open={validateModalVisible}
        onCancel={() => {
          setValidateModalVisible(false);
          validateForm.resetFields();
          setSelectedPayment(null);
        }}
        onOk={handleValidate}
        okText="Validate"
        okButtonProps={{ loading: validating, icon: <CheckCircleOutlined /> }}
        width={500}
        destroyOnClose
      >
        {selectedPayment && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Payment summary */}
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Reference" span={2}>
                <Text copyable style={{ fontSize: 12 }}>
                  {selectedPayment.paymentReference}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Student">
                {selectedPayment.studentName || (
                  <Text type="secondary">Unknown</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong>UGX {formatCurrency(selectedPayment.amount)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Method">
                {selectedPayment.paymentMethod || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {selectedPayment.paymentDate
                  ? dayjs(selectedPayment.paymentDate).format("DD MMM YYYY")
                  : "—"}
              </Descriptions.Item>
            </Descriptions>

            {/* Context alerts */}
            {!isSuspense && (
              <Alert
                type="info"
                showIcon
                message="This payment looks valid. Confirm to allocate it, or update the code/purpose below if needed."
              />
            )}
            {isSuspense && (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message="Payment in Suspense"
                description="Provide the correct purpose and payment code so the system can allocate this payment properly."
              />
            )}

            {/* Validation form */}
            <Form form={validateForm} layout="vertical" size="small">
              <Form.Item
                label="Purpose"
                name="purpose"
                rules={
                  isSuspense
                    ? [
                        {
                          required: true,
                          message: "Please select the payment purpose",
                        },
                      ]
                    : []
                }
              >
                <Select
                  placeholder="Select payment purpose"
                  options={PURPOSE_OPTIONS}
                  allowClear
                />
              </Form.Item>
              <Form.Item
                label="Correct Payment Code"
                name="correctCode"
                extra={
                  isSuspense
                    ? "Enter the correct SPC, TPC, BOTPC, CFPC, or APC for this payment."
                    : "Leave blank to use the existing code."
                }
              >
                <Input placeholder="e.g. SPC12345 or TPC98765" />
              </Form.Item>
              <Form.Item label="Notes" name="notes">
                <Input.TextArea
                  rows={2}
                  placeholder="Optional validation notes"
                />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>
    </>
  );
};

export default ReconciliationDetailDrawer;
